import {
    LaunchpadDescriptionModifier,
    LaunchpadDescriptionModifierContext,
} from 'modules/common/components/Layout/LaunchpadTasks/useLaunchpadSchema/interfaces';
import {
    LaunchpadItem,
    LaunchpadSubtask,
} from 'modules/common/components/LaunchpadProgress/LaunchpadProgress.interface';
import { Member } from 'modules/common/interfaces/member';
import { BusinessVerificationTaskName } from 'modules/Onboarding/BusinessVerificationFlow/constants';
import { S_CORP_ELECTION_UPLOAD_TASK } from 'modules/common/components/Layout/LaunchpadTasks/useLaunchpadSchema/constants';

/**
 * Modifiers for top-level task descriptions.
 * Each modifier can dynamically update a task's description based on context.
 * Key is the template_id (original schema ID).
 */
const TASK_DESCRIPTION_MODIFIERS: Record<string, LaunchpadDescriptionModifier> = {};

/**
 * Modifiers for subtask descriptions.
 * Each modifier can dynamically update a subtask's description based on context.
 * Key is the template_id (original schema ID).
 */
const SUBTASK_DESCRIPTION_MODIFIERS: Record<string, LaunchpadDescriptionModifier> = {
    [BusinessVerificationTaskName.BUSINESS_VERIFICATION_UPLOAD_ARTICLES_OF_ORGANIZATION_MIDDESK]: () => {
        return 'Submit and Confirm your Articles of Organization. Collective will try to pull this for you automatically from the Secretary of State. If you do not see a copy of your articles in the next screen, please upload your own copy of the Articles of Organization.';
    },
    [S_CORP_ELECTION_UPLOAD_TASK]: () => {
        return 'Print your pre-filled S Corp election form, sign, and send it back to us. The IRS requires that this form be signed in ink.';
    },
};

/**
 * Applies description modifiers to update task and subtask descriptions dynamically.
 * @param launchpadItems - The built launchpad items to modify
 * @param allTasksData - All tasks data from the API
 * @param member - Current member data
 * @returns Launchpad items with updated descriptions where modifiers apply
 */
export function applyDescriptionModifiers(
    launchpadItems: LaunchpadItem[],
    allTasksData: Record<string, Partial<LaunchpadItem>>,
    member: Member | undefined
): LaunchpadItem[] {
    return launchpadItems.map((item): LaunchpadItem => {
        // Check if there's a modifier for this task
        const taskModifier = TASK_DESCRIPTION_MODIFIERS[item.template_id];
        const taskContext: LaunchpadDescriptionModifierContext = {
            launchpadItem: item,
            allTasksData,
            member,
        };

        const newTaskDescription = taskModifier?.(taskContext);

        // Apply modifiers to subtasks
        const modifiedSubtasks = item.subtasks.map((subtask): LaunchpadSubtask => {
            const subtaskModifier = SUBTASK_DESCRIPTION_MODIFIERS[subtask.template_id];
            if (!subtaskModifier) {
                return subtask;
            }

            const subtaskContext: LaunchpadDescriptionModifierContext = {
                launchpadItem: item,
                launchpadSubtask: subtask,
                allTasksData,
                member,
            };

            const newSubtaskDescription = subtaskModifier(subtaskContext);
            if (newSubtaskDescription !== undefined) {
                return {
                    ...subtask,
                    description: newSubtaskDescription,
                };
            }

            return subtask;
        });

        return {
            ...item,
            description: newTaskDescription !== undefined ? newTaskDescription : item.description,
            subtasks: modifiedSubtasks,
        };
    });
}
